import { useCallback, useEffect, useState, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { DeviceSearchResponse, DeviceSearchRow } from '../types';

/**
 * Plan 94 / Plan 101 — client for the scope-checked GET /devices/search
 * endpoint.
 *
 * Server-side scope filtering, facet option lists, and page-number pagination
 * (limit/offset + total) so the client never holds the full fleet. Returns the
 * current page of rows (not accumulated) plus the total for paging controls.
 *
 * Plan 101: typed filters are forwarded generically — every entry in
 * `filters` becomes an `f.<key>=<value>` wire param — and `sort` is the
 * serialized multi-level stack (`key:dir,key:dir`, ≤3 levels). Both are
 * allowlist-validated server-side; the client validates against the column
 * registry before they get here.
 */
export interface DeviceSearchParams {
  q?: string;
  /** Typed filters: key (no `f.` prefix) → raw wire value. */
  filters?: Record<string, string>;
  /** Serialized sort stack (`key:dir,...`) or a legacy single key. */
  sort?: string;
  /** Legacy single-key sort direction (paired with a bare `sort` key). */
  dir?: 'asc' | 'desc';
  // Pre-101 facet params (the Fleet Explorer still drives these; the server
  // aliases them onto the typed filters).
  entity_prefix?: string;
  app_owner_uid?: string;
  app_location_id?: string;
  app_hvac_id?: string;
  context_status?: string;
  has_app_context?: string;
  page?: number; // 1-based
  pageSize?: number; // default 20
}

type Facets = DeviceSearchResponse['facets'];
export type TagFilterChanges = NonNullable<DeviceSearchResponse['tag_filter_changes']>;

const DEFAULT_PAGE_SIZE = 20;

/**
 * Translate search params into the wire params /devices/search expects
 * (limit/offset paging, `f.<key>` typed filters, empty values dropped).
 *
 * Exported so callers that need a window at an arbitrary position — FS-542's
 * Previous/Next, which reads the rows either side of one index — can reuse the
 * filter translation and override `limit`/`offset` instead of re-deriving them
 * from a page number they don't have.
 */
export const deviceSearchWire = (params: DeviceSearchParams): Record<string, string | number> => {
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const page = Math.max(params.page ?? 1, 1);
  const wire: Record<string, string | number> = { limit: pageSize, offset: (page - 1) * pageSize };
  ([
    'q',
    'entity_prefix',
    'app_owner_uid',
    'app_location_id',
    'app_hvac_id',
    'context_status',
    'has_app_context',
    'sort',
    'dir',
  ] as const).forEach((k) => {
    const v = params[k];
    if (v !== undefined && v !== null && v !== '') wire[k] = v;
  });
  for (const [key, value] of Object.entries(params.filters ?? {})) {
    if (value !== '') wire[`f.${key}`] = value;
  }
  return wire;
};

/** One /devices/search request. Throws; callers own the error surface. */
export const fetchDeviceSearch = async (
  wire: Record<string, string | number>,
  signal?: AbortSignal,
): Promise<DeviceSearchResponse> => {
  const res = await axiosInstance.get<DeviceSearchResponse>(
    `${config.devicesApiUrl}/devices/search`,
    signal ? { params: wire, signal } : { params: wire },
  );
  return res.data;
};

export interface DeviceSearchOptions {
  /**
   * FS-889: false while the page does not yet know what to search for (a
   * saved report is still loading). No request is made, one in flight is
   * aborted, and the rows of the previous view are not reported as current —
   * the caller shows its loading state instead of the old list.
   */
  enabled?: boolean;
}

export function useDeviceSearch(
  params: DeviceSearchParams,
  /**
   * FS-501: called when the server resolved a tag filter through the merge
   * alias table. The stored filter still holds the retired tag id, so the
   * caller rewrites it (and its saved prefs) once instead of relying on the
   * server to re-resolve on every load — and can tell the operator when a tag
   * in their saved view was deleted outright.
   */
  onTagFilterChanges?: (changes: TagFilterChanges) => void,
  { enabled = true }: DeviceSearchOptions = {},
) {
  const [rows, setRows] = useState<DeviceSearchRow[]>([]);
  // FS-882: the request the rows on hand answer. `loading` turns true inside
  // the fetch effect, one render after the params change, so on that render it
  // still reads false over the previous request's rows; comparing keys does
  // not lag.
  const [rowsKey, setRowsKey] = useState<string | null>(null);
  const [facets, setFacets] = useState<Facets>({});
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const page = Math.max(params.page ?? 1, 1);

  // Stable key for the effect: the wire params themselves, serialized.
  const key = JSON.stringify(deviceSearchWire(params));

  // Held in a ref so a caller passing an inline callback cannot re-trigger the
  // fetch effect on every render.
  const onTagChangesRef = useRef(onTagFilterChanges);
  onTagChangesRef.current = onTagFilterChanges;

  // FS-776: every keystroke starts a request, and a broad early query ("3")
  // can answer after a narrow later one ("352"). Only the newest request may
  // touch state; an older one is aborted and, if it answers anyway, ignored.
  const latestRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const fetchPage = useCallback(async () => {
    // A refetch asked for while disabled (an edit saved during a report
    // load) waits for the enabling render, which fetches anyway.
    if (!enabledRef.current) return;
    const seq = ++latestRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const isLatest = () => seq === latestRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDeviceSearch(JSON.parse(key), controller.signal);
      if (!isLatest()) return;
      setRows(data.devices);
      setRowsKey(key);
      setFacets(data.facets ?? {});
      setTotal(data.total ?? 0);
      const tagChanges = data.tag_filter_changes;
      if (tagChanges && Object.keys(tagChanges).length > 0) {
        onTagChangesRef.current?.(tagChanges);
      }
    } catch (err) {
      if (!isLatest()) return;
      console.error('Device search failed:', err);
      setError('Failed to search devices');
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [key]);

  useEffect(() => {
    if (!enabled) {
      // Retire the request in flight: bump the sequence first so its
      // cancellation (or a late answer) can never touch state.
      latestRef.current++;
      abortRef.current?.abort();
      abortRef.current = null;
      return;
    }
    fetchPage();
  }, [fetchPage, enabled]);

  // Unmounting retires the sequence as well as aborting the request: an
  // answer that arrives anyway (FS-889: the account changed under the page)
  // is then older than the latest sequence and can neither set state nor
  // heal a tag filter into the next account's URL.
  useEffect(
    () => () => {
      latestRef.current++;
      abortRef.current?.abort();
    },
    [],
  );

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // refetch: re-run the current search (e.g. after an edit saves).
  // rowsCurrent: the rows answer these params — false from the render the
  // params change until their answer lands, and after a failed search.
  // While disabled the hook reports an empty, loading list: the rows on hand
  // answer a view that is no longer the one on screen.
  if (!enabled) {
    return {
      rows: [] as DeviceSearchRow[],
      rowsCurrent: false,
      facets: {} as Facets,
      total: 0,
      loading: true,
      error: null as string | null,
      page,
      pageSize,
      pageCount: 1,
      refetch: fetchPage,
    };
  }
  return {
    rows,
    rowsCurrent: rowsKey === key,
    facets,
    total,
    loading,
    error,
    page,
    pageSize,
    pageCount,
    refetch: fetchPage,
  };
}
